import { useEffect, useState, useCallback } from 'react'
import { useSurvey } from '../contexts/SurveyProvider'
import { Link } from 'react-router-dom'
import { useApi } from '../contexts/ApiProvider'
import {
  Dialog,
  DialogBody,
  HotkeysProvider,
  NonIdealState,
  Spinner,
} from '@blueprintjs/core'
import './SegmentationExplorerDialog.css'
import { AppToaster } from './toaster'
import { errorObjectToString } from '../ApiClient'
import { SegmentationsSelector } from './segmentationexplorer/SegmentationsSelector'
import { WeightsGroupTabs } from './segmentationexplorer/WeightsGroupTabs'
import { InfoGroupTabs } from './segmentationexplorer/InfoGroupTabs'
import { useSegmentationPriorities } from '../contexts/SegmentationPrioritiesProvider'
import { DialogNavbar } from './segmentationexplorer/DialogNavBar'

/**
 * SegmentationExplorerDialog component displays the segmentation explorer dialog.
 *
 * It manages segmentation selection, fetches extra data, and toggles between
 * info and weights views.
 *
 * @returns {JSX.Element} The rendered dialog component.
 */
export function SegmentationExplorerDialog() {
  const { setSurveyDialogOpen, surveyDialogOpen, surveyId } = useSurvey()
  const { segmentations } = useSegmentationPriorities()
  const [selectedSegmentation, setSelectedSegmentation] = useState()
  const [selectedGroupTabs, setSelectedGroupTabs] = useState('info')
  const [selectedSegmentations, setSelectedSegmentations] = useState([])
  const [isLoadingSegmentation, setIsLoadingSegmentation] = useState(false)
  const api = useApi()

  const onClose = () => setSurveyDialogOpen()

  const isOpen = surveyDialogOpen === 'SegmentationExplorerDialog'

  /**
   * selectSegmentation fetches detailed data for a given segmentation.
   *
   * @param {Object} segmentation - The segmentation object to fetch data for.
   */
  const selectSegmentation = useCallback(
    async segmentation => {
      if (!segmentation) {
        setSelectedSegmentation(undefined)
        return
      }
      setIsLoadingSegmentation(true)
      const response = await api.get(
        `/survey/${surveyId}/segmentation/${segmentation.id}`
      )
      if (response.ok) {
        setSelectedSegmentation(response.body)
      } else {
        const message = errorObjectToString(response.body.messages.json)
        AppToaster.show({ message, intent: 'danger', icon: 'error' })
      }
      setIsLoadingSegmentation(false)
    },
    [api, surveyId]
  )

  useEffect(() => {
    if (
      isOpen &&
      segmentations &&
      segmentations.length > 0 &&
      !selectedSegmentation
    ) {
      selectSegmentation(segmentations[0])
    }
  }, [isOpen, segmentations, selectSegmentation, selectedSegmentation])

  // Switch to weights view.
  const showWeightTabs = () => {
    setSelectedGroupTabs('weights')
  }
  // Switch to info view.
  const showInfoTabs = () => {
    setSelectedGroupTabs('info')
  }

  /**
   * Handle multi-segmentation selection for weights mode
   * @param {Array} newSelectedSegmentations - Array of selected segmentations
   */
  const handleMultiSelectionChange = useCallback(newSelectedSegmentations => {
    setSelectedSegmentations(newSelectedSegmentations)
  }, [])

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        <DialogNavbar
          selectedGroupTabs={selectedGroupTabs}
          showInfoTabs={showInfoTabs}
          showWeightTabs={showWeightTabs}
        />
      }
      icon="inherited-group"
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className="segmentation-explorer-dialog"
    >
      <DialogBody>
        {segmentations ? (
          segmentations.length ? (
            <div className="segmentation-explorer-dialog-body-container">
              <div
                className="segmentation-explorer-dialog-body-segmentation-selector"
                style={{ overflowY: 'auto', background: '#ffffff' }}
              >
                <SegmentationsSelector
                  // Props for single selection mode (Info tab)
                  selectSegmentation={
                    selectedGroupTabs === 'info'
                      ? selectSegmentation
                      : undefined
                  }
                  selectedSegmentation={
                    selectedGroupTabs === 'info'
                      ? selectedSegmentation
                      : undefined
                  }
                  // Props for multi-selection mode (both tabs)
                  onSelectionChange={handleMultiSelectionChange}
                  selectedSegmentations={selectedSegmentations}
                  multiSelect={true}
                  showSearch={true}
                />
              </div>
              <div
                style={{
                  width: '65vw',
                  marginLeft: '20px',
                  // overflow: 'hidden',
                }}
              >
                {selectedGroupTabs === 'info' ? (
                  <InfoGroupTabs
                    selectedSegmentation={selectedSegmentation}
                    isLoading={isLoadingSegmentation}
                  />
                ) : (
                  <HotkeysProvider>
                    <WeightsGroupTabs
                      selectedSegmentations={selectedSegmentations}
                      onSelectedSegmentationsChange={handleMultiSelectionChange}
                    />
                  </HotkeysProvider>
                )}
              </div>
            </div>
          ) : (
            <div>
              No segmentations yet.{' '}
              <Link to={`/survey/${surveyId}/grouper`} onClick={onClose}>
                Create some.
              </Link>
            </div>
          )
        ) : (
          <NonIdealState
            icon={<Spinner intent="primary" />}
            title="Loading data"
            description="Please wait while load segmentations."
          />
        )}
      </DialogBody>
    </Dialog>
  )
}
